import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import type { Permission, Role } from '@parkline/shared';

import { ROLE_LABELS, can as canByRoles } from './shared';

import { LOGIN_PATH, TOKEN_KEY, USER_KEY, clearSession, get, post, readToken } from './api';
import { Spinner } from '../components/Spinner';

/** Профиль, который бэкенд отдаёт при логине и на /auth/me. */
export interface AuthProfile {
  id: string;
  login: string;
  fullName: string;
  roles: Role[];
  driverId?: string | null;
}

interface LoginResponse {
  /** Разные ветки бэкенда называют поле по-разному — принимаем оба. */
  token?: string;
  accessToken?: string;
  user: AuthProfile;
}

interface AuthContextValue {
  user: AuthProfile | null;
  roles: Role[];
  loading: boolean;
  login: (login: string, password: string) => Promise<void>;
  logout: () => void;
  can: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const NO_TOKEN_ERROR = 'Сервер не вернул токен доступа';

function readStoredUser(): AuthProfile | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<AuthProfile>;
    if (!parsed?.id || !Array.isArray(parsed.roles)) {
      return null;
    }
    return parsed as AuthProfile;
  } catch {
    // Битый JSON в localStorage не должен ронять приложение — считаем, что профиля нет.
    localStorage.removeItem(USER_KEY);
    return null;
  }
}

function persistUser(user: AuthProfile): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthProfile | null>(() => (readToken() ? readStoredUser() : null));
  const [loading, setLoading] = useState<boolean>(() => Boolean(readToken()));

  // При старте с сохранённым токеном показываем кэшированный профиль сразу,
  // а актуальный подтягиваем с /auth/me. Недоступность эндпоинта не выкидывает
  // пользователя из системы — просроченный токен отсеет 401-интерцептор.
  useEffect(() => {
    if (!readToken()) {
      return;
    }
    let active = true;
    get<AuthProfile>('/auth/me')
      .then((profile) => {
        if (!active || !profile?.id) {
          return;
        }
        persistUser(profile);
        setUser(profile);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(
    async (loginName: string, password: string) => {
      const response = await post<LoginResponse>('/auth/login', {
        login: loginName,
        password,
      });
      const token = response.token ?? response.accessToken;
      if (!token || !response.user) {
        throw new Error(NO_TOKEN_ERROR);
      }
      localStorage.setItem(TOKEN_KEY, token);
      persistUser(response.user);
      queryClient.clear();
      setUser(response.user);
      setLoading(false);
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    clearSession();
    queryClient.clear();
    setUser(null);
    navigate(LOGIN_PATH, { replace: true });
  }, [navigate, queryClient]);

  const roles = useMemo<Role[]>(() => user?.roles ?? [], [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      roles,
      loading,
      login,
      logout,
      can: (permission: Permission) => canByRoles(roles, permission),
    }),
    [user, roles, loading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth вызван вне AuthProvider');
  }
  return context;
}

function FullScreenSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="lg" label="Загрузка профиля…" />
    </div>
  );
}

/** Гейт маршрута: неавторизованного уводим на форму входа, запомнив адрес. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading && !user) {
    return <FullScreenSpinner />;
  }
  if (!user) {
    return <Navigate to={LOGIN_PATH} replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  return <>{children ?? <Outlet />}</>;
}

/** Гейт по праву: вместо белого экрана показываем понятное объяснение. */
export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children?: ReactNode;
}) {
  const { can, roles } = useAuth();
  if (!can(permission)) {
    return <ForbiddenScreen permission={permission} roles={roles} />;
  }
  return <>{children ?? <Outlet />}</>;
}

export function ForbiddenScreen({
  permission,
  roles,
}: {
  permission: Permission;
  roles: readonly Role[];
}) {
  const roleNames = roles.map((role) => ROLE_LABELS[role]).join(', ');
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
      <ShieldAlert className="mb-4 h-12 w-12 text-blocked" aria-hidden />
      <h1 className="text-xl font-semibold text-slate-900">Недостаточно прав</h1>
      <p className="mt-2 max-w-md text-sm text-slate-600">
        Для этого раздела нужно право <code className="rounded bg-slate-200 px-1">{permission}</code>.
        {roleNames ? ` Ваши роли: ${roleNames}.` : ' Роли не назначены.'}
      </p>
      <p className="mt-1 text-sm text-slate-500">
        Обратитесь к администратору системы, если доступ необходим для работы.
      </p>
    </div>
  );
}
